import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { buildApiUrl } from '../utils/api';
import { logger } from '../utils/logger';

/**
 * Tela de retorno do Stripe Checkout (success_url).
 *
 * Quando o cliente conclui o checkout, o Stripe envia o webhook
 * `checkout.session.completed` quase imediatamente, mas há latência.
 * Esta tela faz polling em /api/check-trial até detectar que a empresa
 * saiu de `pending_checkout`. Se o webhook tardar, exibe um botão de
 * reload manual depois de ~30s.
 */
const OnboardingSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('confirming'); // confirming | active | timeout | error
  const pollingRef = useRef(null);

  useEffect(() => {
    const auth = getAuth();
    const startedAt = Date.now();
    const TIMEOUT_MS = 30000;
    const INTERVAL_MS = 2000;

    const tick = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          // Sessão pode ter expirado durante o checkout — manda pra login
          navigate('/login');
          return;
        }
        const idToken = await user.getIdToken(true);

        // Recupera o companyId do localStorage / sessionStorage / claims
        // (várias partes do app usam selectedCompany no localStorage)
        let companyId = null;
        try {
          const stored = JSON.parse(localStorage.getItem('selectedCompany') || 'null');
          companyId = stored?.companyId || stored?.id;
        } catch (_) {}

        if (!companyId) {
          // Sem companyId conhecido, busca via /companies do usuário
          const meResp = await fetch(buildApiUrl('/api/me'), {
            headers: { Authorization: 'Bearer ' + idToken },
          }).catch(() => null);
          if (meResp && meResp.ok) {
            const me = await meResp.json();
            companyId = me?.companies?.[0]?.companyId;
          }
        }

        if (!companyId) {
          setStatus('error');
          return;
        }

        const resp = await fetch(buildApiUrl(`/api/check-trial/${companyId}`), {
          headers: { Authorization: 'Bearer ' + idToken },
        });
        const data = await resp.json();
        logger.log('[OnboardingSuccess] check-trial:', resp.status, data);

        // 200 com mensagem de "ativa" ou "trial válido" → liberado
        if (resp.status === 200) {
          setStatus('active');
          clearInterval(pollingRef.current);
          // Pequena pausa para o usuário ver o checkmark
          setTimeout(() => navigate('/chat'), 1200);
          return;
        }

        // Ainda pending — continua tentando até timeout
        if (Date.now() - startedAt > TIMEOUT_MS) {
          setStatus('timeout');
          clearInterval(pollingRef.current);
        }
      } catch (err) {
        console.error('[OnboardingSuccess] erro no polling:', err);
        if (Date.now() - startedAt > TIMEOUT_MS) {
          setStatus('error');
          clearInterval(pollingRef.current);
        }
      }
    };

    // Primeira chamada imediata, depois polling
    tick();
    pollingRef.current = setInterval(tick, INTERVAL_MS);
    return () => clearInterval(pollingRef.current);
  }, [navigate, location.search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
        {status === 'confirming' && (
          <>
            <Loader2 className="mx-auto mb-4 animate-spin text-[#FFC107]" size={48} />
            <h1 className="text-xl font-semibold mb-2 text-[#0E0E12]">Confirmando seu pagamento…</h1>
            <p className="text-gray-600">Estamos liberando seu acesso. Isso costuma levar poucos segundos.</p>
          </>
        )}
        {status === 'active' && (
          <>
            <CheckCircle2 className="mx-auto mb-4 text-green-500" size={48} />
            <h1 className="text-xl font-semibold mb-2 text-[#0E0E12]">Tudo pronto!</h1>
            <p className="text-gray-600">Seu acesso foi liberado. Redirecionando para a plataforma…</p>
          </>
        )}
        {status === 'timeout' && (
          <>
            <AlertCircle className="mx-auto mb-4 text-amber-500" size={48} />
            <h1 className="text-xl font-semibold mb-2 text-[#0E0E12]">Quase lá!</h1>
            <p className="text-gray-600 mb-4">
              O Stripe ainda está processando. Em geral chega em 1 minuto.
              Se levar mais que isso, atualize a página.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-[#FFC107] hover:bg-[#1A1A20] text-white py-2 px-6 rounded-lg font-semibold transition-colors"
            >
              Atualizar
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
            <h1 className="text-xl font-semibold mb-2 text-[#0E0E12]">Não conseguimos confirmar</h1>
            <p className="text-gray-600 mb-4">
              Se você completou o pagamento, ele será processado em breve.
              Tente acessar a plataforma normalmente.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="bg-[#FFC107] hover:bg-[#1A1A20] text-white py-2 px-6 rounded-lg font-semibold transition-colors"
            >
              Ir para o login
            </button>
          </>
        )}
        <p className="mt-6 text-sm text-gray-500">
          Dúvidas? <a href="mailto:contato@iatconsulting.com.br" className="text-[#0E0E12] hover:underline">contato@iatconsulting.com.br</a>
        </p>
      </div>
    </div>
  );
};

export default OnboardingSuccess;
